import React, { useEffect, useState, useRef } from 'react';
import { Header } from '../components/Header';
import { Card } from '../components/Card';
import { Button } from '../components/Button';
import { Icon } from '../components/Icon';
import { apiClient } from '../api/client';
import { Subscription } from '../types';
import { useAppStore } from '../store/useAppStore';
import './SubscriptionScreen.css';
import { useSwipeBack } from '../hooks/useSwipeBack';
import { useTranslation } from 'react-i18next';

export const SubscriptionScreen: React.FC = () => {
  const { setCurrentPage, setSettings, settings } = useAppStore();
  const { t, i18n } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [loading, setLoading] = useState(true);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPromoModal, setShowPromoModal] = useState(false);
  const [promoCode, setPromoCode] = useState('');
  const [promoError, setPromoError] = useState('');
  
  // Состояние для drag toggle
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartX, setDragStartX] = useState(0);
  const [dragOffset, setDragOffset] = useState(0); // Визуальный offset (ограниченный)
  const [realDragOffset, setRealDragOffset] = useState(0); // Реальный offset для логики

  useEffect(() => {
    loadSubscription();
  }, []);

  // Обработка drag на уровне документа
  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e: MouseEvent) => {
      const offset = e.clientX - dragStartX;
      setRealDragOffset(offset); // Реальный offset без ограничений
      const clampedOffset = Math.max(-22, Math.min(22, offset));
      setDragOffset(clampedOffset); // Визуальный offset
    };

    const handleTouchMove = (e: TouchEvent) => {
      const offset = e.touches[0].clientX - dragStartX;
      setRealDragOffset(offset); // Реальный offset без ограничений
      const clampedOffset = Math.max(-22, Math.min(22, offset));
      setDragOffset(clampedOffset); // Визуальный offset
    };

    const handleMouseUp = () => {
      handleToggleDragEnd();
    };

    const handleTouchEnd = () => {
      handleToggleDragEnd();
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    document.addEventListener('touchmove', handleTouchMove);
    document.addEventListener('touchend', handleTouchEnd);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, [isDragging, dragStartX]);

  const loadSubscription = async () => {
    try {
      setLoading(true);
      const data = await apiClient.getSubscription();
      setSubscription(data.subscription);
      setEmail(data.subscription.email || '');
      
      // Обновляем settings в глобальном сторе для работы премиум-функций
      const status = data.subscription.status;
      setSettings({
        ...settings,
        subscription_status: status,
        subscription_expires_at: data.subscription.subscription_end || undefined,
      });
    } catch (error) {
      console.error('Error loading subscription:', error);
      alert(t('subscriptionScreen.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const handleCancelAutorenew = async () => {
    try {
      setIsSubmitting(true);
      await apiClient.cancelSubscriptionAutorenew();
      await loadSubscription();
    } catch (error: any) {
      console.error('Error canceling autorenew:', error);
      alert(error?.message || t('subscriptionScreen.cancelAutorenewError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEnableAutorenew = async () => {
    try {
      setIsSubmitting(true);
      await apiClient.enableSubscriptionAutorenew();
      await loadSubscription();
    } catch (error: any) {
      console.error('Error enabling autorenew:', error);
      alert(error?.message || t('subscriptionScreen.enableAutorenewError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  // Обработчики для drag toggle
  const handleToggleDragStart = (e: React.MouseEvent | React.TouchEvent) => {
    if (isSubmitting) return;
    
    setIsDragging(true);
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    setDragStartX(clientX);
    setDragOffset(0);
    setRealDragOffset(0);
  };

  const handleToggleDragMove = (e: React.MouseEvent | React.TouchEvent) => {
    if (!isDragging) return;
    
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const offset = clientX - dragStartX;
    
    // Сохраняем реальный offset без ограничений для логики
    setRealDragOffset(offset);
    
    // Ограничиваем offset для визуального отображения в пределах [-22, 22]
    const clampedOffset = Math.max(-22, Math.min(22, offset));
    setDragOffset(clampedOffset);
  };

  const handleToggleDragEnd = async () => {
    if (!isDragging) return;
    
    setIsDragging(false);
    
    // Используем реальный offset для логики (может быть больше 22px)
    // Если перетащили достаточно далеко (более 10px), переключаем по направлению
    if (Math.abs(realDragOffset) > 10) {
      const shouldEnable = realDragOffset > 0; // Вправо = включить
      const isCurrentlyEnabled = subscription?.subscription_autorenew;
      
      // Переключаем только если направление соответствует намерению
      if (shouldEnable && !isCurrentlyEnabled) {
        // Тянем вправо и выключено -> включаем
        await handleEnableAutorenew();
      } else if (!shouldEnable && isCurrentlyEnabled) {
        // Тянем влево и включено -> выключаем
        await handleCancelAutorenew();
      }
    }
    
    setDragOffset(0);
    setRealDragOffset(0);
  };

  const handleToggleClick = async (e: React.MouseEvent) => {
    // Игнорируем клик если был drag
    if (Math.abs(realDragOffset) > 2) {
      return;
    }
    
    if (subscription?.subscription_autorenew) {
      await handleCancelAutorenew();
    } else {
      await handleEnableAutorenew();
    }
  };

  const handleUpdateEmail = async () => {
    if (!email || !email.includes('@')) {
      alert(t('subscriptionScreen.emailInvalid'));
      return;
    }

    try {
      setIsSubmitting(true);
      await apiClient.updateSubscriptionEmail(email);
      setShowEmailModal(false);
      await loadSubscription();
    } catch (error: any) {
      console.error('Error updating email:', error);
      alert(error?.message || t('subscriptionScreen.emailUpdateError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleApplyPromo = async () => {
    if (!promoCode || !promoCode.trim()) {
      setPromoError(t('subscriptionScreen.promoEmpty'));
      return;
    }

    try {
      setIsSubmitting(true);
      setPromoError('');
      const result = await apiClient.applyPromoCode(promoCode.trim());
      alert(result.message);
      setShowPromoModal(false);
      setPromoCode('');
    } catch (error: any) {
      console.error('Error applying promo:', error);
      setPromoError(error?.message || t('subscriptionScreen.promoApplyError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'lifetime_premium':
      case 'premium':
        return '#34C759';
      case 'expired':
        return '#FF3B30';
      default:
        return '#8E8E93';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'lifetime_premium':
        return t('subscriptionScreen.statusLifetime');
      case 'premium':
        return t('subscriptionScreen.statusPremium');
      case 'expired':
        return t('subscriptionScreen.statusExpired');
      default:
        return t('subscriptionScreen.statusUnknown');
    }
  };

  const getProviderName = (provider: string | null) => {
    switch (provider) {
      case 'stripe':
        return 'Stripe';
      default:
        return t('subscription.notSpecified');
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '—';
    const date = new Date(dateString);
    const locale = i18n.language === 'ru' ? 'ru-RU' : i18n.language?.startsWith('en') ? 'en-US' : i18n.language || 'en';
    return date.toLocaleDateString(locale, {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  useSwipeBack(containerRef, () => setCurrentPage('settings'));

  if (loading) {
    return (
      <div ref={containerRef} className="subscription-screen">
        <Header title={t('subscriptionScreen.title')} onBack={() => setCurrentPage('settings')} />
        <div style={{ padding: '20px', textAlign: 'center' }}>
          <p style={{ color: '#8E8E93' }}>{t('subscriptionScreen.loading')}</p>
        </div>
      </div>
    );
  }

  if (!subscription) {
    return (
      <div ref={containerRef} className="subscription-screen">
        <Header title={t('subscriptionScreen.title')} onBack={() => setCurrentPage('settings')} />
        <div style={{ padding: '20px', textAlign: 'center' }}>
          <p style={{ color: '#FF3B30' }}>{t('subscriptionScreen.loadErrorText')}</p>
          <Button onClick={loadSubscription}>{t('subscriptionScreen.retry')}</Button>
        </div>
      </div>
    );
  }

  const isPremium = subscription.status === 'premium' || subscription.status === 'lifetime_premium';
  const isLifetime = subscription.status === 'lifetime_premium';

  return (
    <div ref={containerRef} className="subscription-screen">
      <Header title={t('subscriptionScreen.title')} onBack={() => setCurrentPage('settings')} />

      <div className="subscription-content">
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          {/* Status Card */}
          <div style={{ padding: 0 }}>
            <div className="subscription-status">
              <div 
                className="subscription-status__badge"
                style={{ backgroundColor: 'transparent', border: `2px solid ${getStatusColor(subscription.status)}40` }}
              >
                <Icon name={subscription.status === 'premium' || subscription.status === 'lifetime_premium' ? 'trophy' : 'user'} size={48} color={getStatusColor(subscription.status)} />
              </div>
              <h2 
                className="subscription-status__title"
                style={{ color: getStatusColor(subscription.status) }}
              >
                {getStatusText(subscription.status)}
              </h2>
              {isPremium && !isLifetime && subscription.subscription_end && (
                <>
                  <p className="subscription-status__date">
                    {t('subscriptionScreen.activeUntil', { date: formatDate(subscription.subscription_end) })}
                  </p>
                  <p 
                    className="subscription-status__change-plan"
                    onClick={() => setCurrentPage('plans')}
                  >
                    {t('subscriptionScreen.changePlan')}
                  </p>
                </>
              )}
              {isLifetime && (
                <p className="subscription-status__date" style={{ color: '#34C759', display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center' }}>
                  <Icon name="infinity" size={16} color="#34C759" /> {t('subscriptionScreen.lifetime')}
                </p>
              )}
            </div>
          </div>

          {/* Details Card */}
          {isPremium && (
            <Card padding="medium" className="neo-static">
              <div className="subscription-details">
              <h3 className="subscription-details__title">{t('subscriptionScreen.detailsTitle')}</h3>
              
              <div className="subscription-detail-item">
                <span className="subscription-detail-item__label">{t('subscriptionScreen.provider')}</span>
                <span className="subscription-detail-item__value">
                  {getProviderName(subscription.subscription_provider)}
                </span>
              </div>

              {!isLifetime && (
                <div 
                  className="subscription-detail-item subscription-detail-item--toggle"
                >
                  <span className="subscription-detail-item__label">{t('subscriptionScreen.autorenew')}</span>
                  <div 
                    className={`toggle-switch ${subscription.subscription_autorenew ? 'toggle-switch--on' : ''} ${isDragging ? 'toggle-switch--dragging' : ''}`}
                    onClick={handleToggleClick}
                    onMouseDown={handleToggleDragStart}
                    onTouchStart={handleToggleDragStart}
                    style={{
                      userSelect: 'none',
                      WebkitUserSelect: 'none',
                      touchAction: 'none'
                    }}
                  >
                    <div 
                      className="toggle-switch__handle"
                      style={{
                        transform: `translateX(${subscription.subscription_autorenew ? 22 + dragOffset : dragOffset}px)`,
                        transition: isDragging ? 'none' : 'all 0.3s ease'
                      }}
                    />
                  </div>
                </div>
              )}

              {subscription.email && (
                <div 
                  className="subscription-detail-item subscription-detail-item--editable"
                  onClick={() => setShowEmailModal(true)}
                >
                  <span className="subscription-detail-item__label">{t('subscriptionScreen.emailForReceipts')}</span>
                  <span className="subscription-detail-item__value">
                    {subscription.email}
                  </span>
                  <Icon name="edit" size={18} color="#007AFF" />
                </div>
              )}
              </div>
            </Card>
          )}
        </div>

        {/* Actions */}
        <div className="subscription-actions">
          <button
            className="neo-raised"
            onClick={() => setShowPromoModal(true)}
            disabled={isSubmitting}
            style={{ 
              width: '100%',
              padding: '14px 28px',
              borderRadius: '16px',
              fontSize: '16px',
              fontWeight: '600',
              border: 'none',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              color: 'var(--color-text-primary)',
              fontFamily: 'inherit'
            }}
          >
            <Icon name="gift" size={18} />
            <span>{t('subscriptionScreen.enterPromo')}</span>
          </button>

          {!isPremium && (
            <Card padding="medium" className="neo-static">
              <div style={{ textAlign: 'center' }}>
                <h3 style={{ margin: '0 0 12px 0', fontSize: '20px' }}>
                  {t('subscriptionScreen.ctaOpenFeatures')}
                </h3>
                <p style={{ margin: '0 0 16px 0', fontSize: '14px', color: 'var(--color-text-secondary)' }}>
                  <span dangerouslySetInnerHTML={{ __html: t('subscriptionScreen.featuresList') }} />
                </p>
                <Button 
                  variant="primary"
                  onClick={() => setCurrentPage('plans')}
                  fullWidth
                >
                  {t('subscriptionScreen.upgradeButton')}
                </Button>
              </div>
            </Card>
          )}
        </div>
      </div>

      {/* Email Modal */}
      {showEmailModal && (
        <div 
          className="modal-overlay modal-overlay--bottom"
          onClick={() => !isSubmitting && setShowEmailModal(false)}
        >
          <div 
            className="modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="modal-title">{t('subscriptionScreen.changeEmailTitle')}</h3>
            <p className="modal-subtitle">
              {t('subscriptionScreen.changeEmailSubtitle')}
            </p>
            
            <input
              type="email"
              className="modal-input neo-inset"
              placeholder="example@email.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={isSubmitting}
            />

            <div className="modal-actions">
              <button 
                className="neo-raised"
                onClick={() => setShowEmailModal(false)}
                disabled={isSubmitting}
                style={{
                  width: '100%',
                  padding: '16px',
                  background: 'var(--neo-bg)',
                  border: 'none',
                  borderRadius: '14px',
                  color: 'var(--color-text-secondary)',
                  fontSize: '16px',
                  fontWeight: '700',
                  cursor: 'pointer',
                }}
              >
                {t('subscriptionScreen.modalCancel')}
              </button>
              <button 
                className="neo-raised"
                onClick={handleUpdateEmail}
                disabled={isSubmitting}
                style={{
                  width: '100%',
                  padding: '16px',
                  background: 'var(--neo-bg)',
                  border: 'none',
                  borderRadius: '14px',
                  color: 'var(--color-primary)',
                  fontSize: '16px',
                  fontWeight: '700',
                  cursor: 'pointer',
                  marginTop: '12px'
                }}
              >
                {isSubmitting ? t('subscriptionScreen.modalSaving') : t('subscriptionScreen.modalSave')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Promo Code Modal */}
      {showPromoModal && (
        <div 
          className="modal-overlay modal-overlay--bottom"
          onClick={() => !isSubmitting && setShowPromoModal(false)}
        >
          <div 
            className="modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="modal-title">{t('subscriptionScreen.promoTitle')}</h3>
            <p className="modal-subtitle">
              {t('subscriptionScreen.promoSubtitle')}
            </p>
            
            <input
              type="text"
              className={`modal-input neo-inset ${promoError ? 'modal-input--error' : ''}`}
              placeholder="PROMO2024"
              value={promoCode}
              onChange={(e) => {
                setPromoCode(e.target.value.toUpperCase());
                setPromoError('');
              }}
              disabled={isSubmitting}
              style={{ textTransform: 'uppercase' }}
            />

            {promoError && (
              <div style={{ 
                color: '#FF3B30', 
                fontSize: '13px', 
                marginTop: '-12px', 
                marginBottom: '12px' 
              }}>
                {promoError}
              </div>
            )}

            <div className="modal-actions">
              <button 
                className="neo-raised"
                onClick={() => {
                  setShowPromoModal(false);
                  setPromoCode('');
                  setPromoError('');
                }}
                disabled={isSubmitting}
                style={{
                  width: '100%',
                  padding: '16px',
                  background: 'var(--neo-bg)',
                  border: 'none',
                  borderRadius: '14px',
                  color: 'var(--color-text-secondary)',
                  fontSize: '16px',
                  fontWeight: '700',
                  cursor: 'pointer',
                }}
              >
                {t('subscriptionScreen.modalCancel')}
              </button>
              <button 
                className="neo-raised"
                onClick={handleApplyPromo}
                disabled={isSubmitting}
                style={{
                  width: '100%',
                  padding: '16px',
                  background: 'var(--neo-bg)',
                  border: 'none',
                  borderRadius: '14px',
                  color: 'var(--color-primary)',
                  fontSize: '16px',
                  fontWeight: '700',
                  cursor: 'pointer',
                  marginTop: '12px'
                }}
              >
                {isSubmitting ? t('subscriptionScreen.promoChecking') : t('subscriptionScreen.promoApply')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

